import { Link } from 'react-router-dom';
import { useFeaturedJobs } from '../../../hooks/unified/useJobsQueries';
import { useViewport } from '../../../hooks/useViewport';
import { env } from '../../../config/env';

export function FeaturedJobsSection() {
  const { data: featuredJobs = [], isLoading, isError } = useFeaturedJobs();
  const { isMobile, prefersReducedMotion } = useViewport();
  
  const displayedJobs = featuredJobs.slice(0, 6);

  const transitionClasses = prefersReducedMotion ? '' : 'transition-all duration-300';
  const hoverClasses = prefersReducedMotion ? '' : 'hover:shadow-xl hover:border-primary-200';

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-8 sm:mb-10 lg:mb-12">
          <div>
            <span className="inline-block px-3 sm:px-4 py-1 bg-primary-100 text-primary-700 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
              Featured Jobs
            </span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
              Latest Opportunities
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-2xl">
              Discover hand-picked positions from top companies looking for talent like you.
            </p>
          </div>
          <Link
            to="/jobs"
            className={`touch-target mt-4 md:mt-0 inline-flex items-center text-primary-600 hover:text-primary-700 font-semibold group ${transitionClasses}`}
          >
            View all jobs
            <svg className={`w-5 h-5 ml-2 ${prefersReducedMotion ? '' : 'group-hover:translate-x-1 transition-transform'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 animate-pulse">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gray-200 rounded-lg sm:rounded-xl"></div>
                  <div className="flex-1">
                    <div className="h-4 sm:h-5 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 sm:h-4 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div className="mt-3 sm:mt-4 space-y-2">
                  <div className="h-3 bg-gray-200 rounded"></div>
                  <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                </div>
                <div className="mt-3 sm:mt-4 flex gap-2">
                  <div className="h-5 sm:h-6 bg-gray-200 rounded-full w-16 sm:w-20"></div>
                  <div className="h-5 sm:h-6 bg-gray-200 rounded-full w-12 sm:w-16"></div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error State */}
        {isError && (
          <div className="text-center py-12 sm:py-16 bg-white rounded-xl sm:rounded-2xl">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-red-100 mb-4">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Unable to Load Jobs</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Please try again later.</p>
            <Link
              to="/jobs"
              className="touch-target inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium text-white bg-primary-600 rounded-lg sm:rounded-xl hover:bg-primary-700 transition-colors"
            >
              Browse All Jobs
            </Link>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !isError && displayedJobs.length === 0 && (
          <div className="text-center py-12 sm:py-16 bg-white rounded-xl sm:rounded-2xl">
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-100 mb-4">
              <svg className="w-7 h-7 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">No Jobs Available</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Check back soon for new opportunities.</p>
          </div>
        )}

        {/* Jobs Grid - Single column on mobile */}
        {!isLoading && !isError && displayedJobs.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {displayedJobs.map((job) => (
              <Link
                key={job.id}
                to={`/jobs/${job.id}`}
                className={`touch-target group bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 border border-gray-100 ${hoverClasses} ${transitionClasses}`}
              >
                <div className="flex items-start gap-3 sm:gap-4">
                  {job.company_logo_url ? (
                    <img 
                      src={job.company_logo_url.startsWith('http') ? job.company_logo_url : `${env.API_BASE_URL}${job.company_logo_url}`} 
                      alt={job.company || 'Company'} 
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg sm:rounded-xl object-cover bg-gray-100"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg sm:rounded-xl flex items-center justify-center text-white font-bold text-lg sm:text-xl ${job.company_logo_url ? 'hidden' : ''}`}>
                    {(job.company || 'C')[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`font-semibold text-gray-900 text-sm sm:text-base ${prefersReducedMotion ? '' : 'group-hover:text-primary-600 transition-colors'} truncate`}>
                      {job.title}
                    </h3>
                    <p className="text-gray-500 text-xs sm:text-sm truncate">{job.company}</p>
                  </div>
                </div>

                <div className="mt-3 sm:mt-4 flex flex-wrap gap-1.5 sm:gap-2">
                  {job.location && (
                    <span className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="truncate max-w-[100px] sm:max-w-none">{job.location}</span>
                    </span>
                  )}
                  {job.job_type && (
                    <span className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 bg-primary-50 text-primary-700 text-xs rounded-full">
                      {job.job_type}
                    </span>
                  )}
                  {job.experience_level && !isMobile && (
                    <span className="inline-flex items-center px-2 sm:px-3 py-0.5 sm:py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                      {job.experience_level}
                    </span>
                  )}
                </div>

                {(job.salary_min || job.salary_max) && (
                  <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100">
                    <span className="text-xs sm:text-sm font-medium text-gray-900">
                      ₹{job.salary_min?.toLocaleString() || '0'} - ₹{job.salary_max?.toLocaleString() || '0'}
                    </span>
                    <span className="text-xs sm:text-sm text-gray-500"> / year</span>
                  </div>
                )}

                <div className="mt-3 sm:mt-4 flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {job.published_at ? new Date(job.published_at).toLocaleDateString() : 'Recently posted'}
                  </span>
                  <span className={`text-primary-600 text-xs sm:text-sm font-medium ${prefersReducedMotion ? '' : 'group-hover:underline'}`}>
                    View Details →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 text-white text-center">
          <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-3 sm:mb-4">
            Ready to Find Your Next Role?
          </h3>
          <p className="text-primary-100 text-sm sm:text-base mb-6 sm:mb-8 max-w-2xl mx-auto mobile-px">
            Create your profile today and get matched with opportunities that fit your skills and career goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Link
              to="/jobs"
              className={`touch-target inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-primary-700 bg-white rounded-lg sm:rounded-xl hover:bg-gray-100 ${prefersReducedMotion ? '' : 'transform hover:scale-105 transition-all duration-300'}`}
            >
              Browse All Jobs
            </Link>
            <Link
              to="/login"
              className={`touch-target inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-semibold text-white bg-white/20 border border-white/30 rounded-lg sm:rounded-xl hover:bg-white/30 ${prefersReducedMotion ? '' : 'transform hover:scale-105 transition-all duration-300'}`}
            >
              Create Profile
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
